import { CouponData } from '@app/models/CouponData';
import { Homeowner } from '@app/models/Homeowner';
import { HomeownersTermsAgreement } from '@app/models/HomeownersTermsAgreement';
import { PaymentMethod } from '@app/models/PaymentMethod';
import { Property } from '@app/models/Property';
import { EventType } from '@app/models/EventType';
import { HOAApiBase } from "@app/services/apiServices/utils/HOAApiBase";
import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpResponse } from "@angular/common/http";

@Injectable({
    providedIn: "root"
})
export class HomeownerApi extends HOAApiBase{

    constructor() {
        super('HomeownerApi');
    }

    public homeownersIdGet(id: number): Observable<Homeowner> {
        this.check({ id }, 'is number', v => Number(v) > 0)

        return this.http.get<Homeowner>(this.createUrl("hoa-gateway", `/homeowners/${id}`));

    }

    public homeownersIdToSUptodateGet(id: number): Observable<Boolean> {
        this.check({ id }, 'is number', v => Number(v) > 0)

        return this.http.get<Boolean>(this.createUrl("hoa-gateway", `/homeowners/${id}/tos-up-to-date`));
    }

    public homeownersIdPost(anonymous: boolean, homeowner: Homeowner): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object')

        const newHomeowner = mapHomeownerToPostHomeowner(homeowner);
        if (anonymous) {
            return this.http.post<Homeowner>(this.createUrl('HOAPass', '/guestPayment/_saveHomeowner'), newHomeowner)
        } else {
            return this.http.post<Homeowner>(this.createUrl('hoa-gateway', '/homeowners'), newHomeowner)
        }
    }

    public guestHomeownersIdPost(anonymous: boolean, homeowner: Homeowner): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof v === 'object')

        const newHomeowner = mapHomeownerToPostHomeowner(homeowner);
        if (anonymous) {
            return this.http.post<Homeowner>(this.createUrl('HOAPass', '/guestPayment/_saveHomeowner'), newHomeowner)
        } else {
            return this.http.post<Homeowner>(this.createUrl('hoa-gateway', '/guest-homeowners'), newHomeowner)
        }
    }

    /**
     * List payment methods in the wallet
     * Returns a list of PaymentMethod objects.\n
     * @param id Unique identifier for the user
     */
    public homeownersIdPaymentMethodsGet(id: number): Observable<PaymentMethod[]> {
        this.check({ id }, 'is number', n => Number(n) > 0)

        return this.http.get<PaymentMethod[]>(this.createUrl('hoa-gateway', `/homeowners/${id}/paymentMethods`))
    }

    /**
     * List Properties
     * Returns a list of Property objects, without the payment history data\n
     * @param id Unique identifier for the user.
     */
    public homeownersIdPropertiesGet(id: number): Observable<Property[]> {
        this.check({ id }, 'is number', n => Number(n) > 0)

        return this.http.get<Property[]>(this.createUrl('hoa-gateway', `/homeowners/${id}/properties`))
    }

    /**
     * Check to see if the Homeowner Id exists on Okta App profile for logged in user
     * returns true if exists on App profile otherwise false
     */
    public homeownersIdExistsGet(): Observable<boolean> {
        return this.http.get<boolean>(this.createUrl('HOAPass', '/secure/v1/user/idExists'))
    }

    /**
     * Save a new Property
     * Save new property information for a user.\n
     * @param id User id to associate with the property
     * @param property Property
     */
    public homeownersIdPropertiesPost(id: number, property: Property): Observable<Property> {
        this.check({ id }, 'is number', n => Number(n) > 0)
        this.check({ property }, 'is present', v => typeof v === 'object' && v != null)

        return this.http.post<Property>(this.createUrl('hoa-gateway', `/homeowners/${id}/properties`), property)
    }

    /**
     * Update a property
     * Update property information for a user.
     * @param id User id to associate with the property
     * @param couponData HOA/PMC/Property Coupon IDs and Party ID
     */
    public homeownersIdCouponUpdatePost(id: number, couponData: CouponData): Observable<HttpResponse<Property>> {
        this.check({ id }, 'is number', n => Number(n) > 0)
        this.check({ couponData }, 'is present', v => typeof v === 'object' && v != null)

        return this.http.post<Property>(this.createUrl('hoa-gateway', `/homeowners/${id}/properties/update`), couponData, { observe: 'response' })
    }

    /**
     * Save Homeowner's Terms Agreement Date
     * @param homeownersTermsAgreement
     * @param anonymous
     */
    public homeownersTermsAgreementPost(anonymous: boolean, homeownersTermsAgreement: HomeownersTermsAgreement): Observable<HomeownersTermsAgreement> {
        this.check({ homeownersTermsAgreement }, 'is present', v => typeof v === 'object' && v != null)

        if (anonymous) {
            return this.http.post<HomeownersTermsAgreement>(this.createUrl('HOAPass', '/v1/registration/_agreements'), homeownersTermsAgreement)
        } else {
            return this.http.post<HomeownersTermsAgreement>(this.createUrl('hoa-gateway', '/homeowners/terms/agreements'), homeownersTermsAgreement)
        }
    }

    public homeownersIdPostAccount(homeowner: Homeowner): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object')

        return this.http.post<Homeowner>(this.createUrl('hoa-gateway', '/homeowners/account'), homeowner)
    }

    public homeownersIdPostAccountEmail(homeowner: Homeowner): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object')

        return this.http.post<Homeowner>(this.createUrl('hoa-gateway', '/homeowners/' + homeowner.id + '/account/email'),
            homeowner.contactInformation.email
        )
    }

    public userPost(homeowner: Homeowner, username: string): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object');
        this.check({ username }, 'is non-empty string', v => typeof v === 'string' && v.length > 0);

        const newHomeowner = mapHomeownerToPostHomeowner(homeowner);
        return this.http.post<Homeowner>(this.createUrl('HOAPass', `/secure/v1/user/${username}/account`), newHomeowner)
    }

    public userResetPasswordPost(homeowner: Homeowner, currentPassword: string, newPassword: string): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object');
        this.check({ currentPassword, newPassword }, 'is non-empty string', v => typeof v === 'string' && v.length > 0);

        const passwordResetData = mapAccountResetPasswordDataToPostResetPasswordData(homeowner, currentPassword, newPassword);
        return this.http.post<Homeowner>(this.createUrl('HOAPass', `/secure/v1/user/reset-password`), passwordResetData)
    }

    public homeownersSendNotificationPost(homeowner: Homeowner, eventType: EventType): Observable<Homeowner> {
        this.check({ homeowner }, 'is present', v => v != null && typeof homeowner === 'object');
        this.check({ eventType }, 'is non-empty string', v => typeof v === 'string' && v.length > 0);

        return this.http.post<Homeowner>(this.createUrl('hoa-gateway', '/homeowners/notify/' + eventType), homeowner)
    }
}

function mapHomeownerToPostHomeowner(homeowner: Homeowner) {
    return {
        "contactInformation": {
            "phones": [
                {
                    "id": homeowner.contactInformation.phones[0].id,
                    "type": "home",
                    "countryCallingCode": homeowner.contactInformation.phones[0].countryCallingCode || "USA",
                    "value": (homeowner.contactInformation.phones[0].value || "8888888888").replace(/[^0-9]/g, ''),
                    "version": homeowner.contactInformation.phones[0].version
                }
            ],
            "email": {
                "value": homeowner.contactInformation.email.value
            },
            "mailingAddress": {
                "city": homeowner.contactInformation.mailingAddress.city,
                "country": "USA",
                "line1": homeowner.contactInformation.mailingAddress.line1,
                "line2": homeowner.contactInformation.mailingAddress.line2,
                "postalCode": homeowner.contactInformation.mailingAddress.postalCode,
                "state": homeowner.contactInformation.mailingAddress.state,
                "unit": homeowner.contactInformation.mailingAddress.unit
            },
            "notifyBySms": homeowner.contactInformation.notifyBySms,
        },
        "firstName": homeowner.firstName,
        "lastName": homeowner.lastName,
        "id": homeowner.id,
        "version": homeowner.version
    };
}

function mapAccountResetPasswordDataToPostResetPasswordData(homeowner: Homeowner, currentPassword: string, newPassword: string) {
    return {
        "homeowner": {
            "contactInformation": {
                "phones": [
                    {
                        "id": homeowner.contactInformation.phones[0].id,
                        "type": "home",
                        "countryCallingCode": homeowner.contactInformation.phones[0].countryCallingCode || "USA",
                        "value": (homeowner.contactInformation.phones[0].value || "8888888888").replace(/[^0-9]/g, ''),
                        "version": homeowner.contactInformation.phones[0].version
                    }
                ],
                "email": {
                    "value": homeowner.contactInformation.email.value
                },
                "mailingAddress": {
                    "city": homeowner.contactInformation.mailingAddress.city,
                    "country": "USA",
                    "line1": homeowner.contactInformation.mailingAddress.line1,
                    "line2": homeowner.contactInformation.mailingAddress.line2,
                    "postalCode": homeowner.contactInformation.mailingAddress.postalCode,
                    "state": homeowner.contactInformation.mailingAddress.state,
                    "unit": homeowner.contactInformation.mailingAddress.unit
                },
                "notifyBySms": homeowner.contactInformation.notifyBySms,
            },
            "firstName": homeowner.firstName,
            "lastName": homeowner.lastName,
            "id": homeowner.id,
            "version": homeowner.version
        },
        "oldPassword": {
            "value": currentPassword
        },
        "newPassword": {
            "value": newPassword
        }
    };
}


